import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePartnerAuth } from '../context/PartnerAuthContext';
import {
  LayoutDashboard, Users, Package, ShoppingCart,
  TrendingUp, AlertCircle, RefreshCw, ChevronLeft,
  ChevronRight, Edit2, Trash2, Check, X, Plus, LogOut,
  Shield, DollarSign, Star, Store
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR') : '-';
const statusColor = (s) => {
  const m = { approved: 'text-green-400', completed: 'text-green-400', pending: 'text-yellow-400', pending_payment: 'text-yellow-400', failed: 'text-red-400', cancelled: 'text-red-400', inactive: 'text-red-400', active: 'text-green-400' };
  return m[s] || 'text-white/60';
};

/* ── KPI Card ── */
const KPICard = ({ label, value, sub, icon: Icon, color }) => (
  <div className="bg-white/5 border border-white/10 rounded-xl p-5">
    <div className="flex items-center justify-between mb-3">
      <span className="text-white/50 text-sm">{label}</span>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}20` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    {sub && <p className="text-white/40 text-xs mt-1">{sub}</p>}
  </div>
);

/* ── Dashboard Tab ── */
const DashboardTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/aug/admin/dashboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setData).finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="text-white/50 text-center py-20">Carregando...</div>;
  if (!data) return <div className="text-red-400 text-center py-20">Erro ao carregar dashboard</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-white">Visão Geral da Plataforma</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Parceiros" value={data.total_partners} sub={`${data.active_partners} ativos`} icon={Users} color="#3b82f6" />
        <KPICard label="Produtos" value={data.total_products} icon={Package} color="#8b5cf6" />
        <KPICard label="Pedidos" value={data.total_orders} sub={`${data.approved_orders} aprovados`} icon={ShoppingCart} color="#f59e0b" />
        <KPICard label="Receita Total" value={fmt(data.total_revenue)} sub={`${fmt(data.month_revenue)} este mês`} icon={DollarSign} color="#22c55e" />
      </div>
      {data.total_commission > 0 && (
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Store className="w-5 h-5 text-yellow-400" />
            <div>
              <p className="text-white/60 text-sm">Comissões acumuladas (parceiros sem plano)</p>
              <p className="text-white text-xs mt-0.5">Parceiros com assinatura ativa: comissão = 0%</p>
            </div>
          </div>
          <span className="text-yellow-400 font-bold text-lg">{fmt(data.total_commission)}</span>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-400" /> Receita</h3>
          <div className="space-y-3">
            <div className="flex justify-between"><span className="text-white/50 text-sm">Esta semana</span><span className="text-white font-medium">{fmt(data.week_revenue)}</span></div>
            <div className="flex justify-between"><span className="text-white/50 text-sm">Este mês</span><span className="text-white font-medium">{fmt(data.month_revenue)}</span></div>
            <div className="flex justify-between"><span className="text-white/50 text-sm">Total histórico</span><span className="text-white font-bold">{fmt(data.total_revenue)}</span></div>
          </div>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-xl p-5">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2"><Star className="w-4 h-4 text-yellow-400" /> Top Parceiros</h3>
          {data.top_partners.length === 0 ? (
            <p className="text-white/30 text-sm">Nenhum pedido registrado ainda</p>
          ) : (
            <div className="space-y-2">
              {data.top_partners.map((p, i) => (
                <div key={p.slug} className="flex justify-between items-center">
                  <span className="text-white/60 text-sm"><span className="text-white/30 mr-2">#{i + 1}</span>{p.slug}</span>
                  <span className="text-green-400 font-medium text-sm">{fmt(p.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center">
          <p className="text-green-400 text-2xl font-bold">{data.approved_orders}</p>
          <p className="text-white/50 text-xs mt-1">Aprovados</p>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 text-center">
          <p className="text-yellow-400 text-2xl font-bold">{data.pending_orders}</p>
          <p className="text-white/50 text-xs mt-1">Pendentes</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
          <p className="text-blue-400 text-2xl font-bold">{data.total_orders - data.approved_orders - data.pending_orders}</p>
          <p className="text-white/50 text-xs mt-1">Outros</p>
        </div>
      </div>
    </div>
  );
};

/* ── Partners Tab ── */
const PartnersTab = ({ token }) => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const PLANS = ['amador', 'profissional', 'expert', 'ocara'];
  const PLAN_NAMES = { amador: 'Parceiro Inicial', profissional: 'Parceiro Profissional', expert: 'Parceiro Avançado', ocara: 'Parceiro Enterprise' };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${API_URL}/api/aug/admin/partners`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setPartners).finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (p) => { setEditId(p.id); setEditForm({ plan_type: p.plan_type, commission_rate: p.commission_rate || 0.05, is_admin: p.is_admin || false, subscription_status: p.subscription_status || 'active' }); };
  const cancelEdit = () => { setEditId(null); setEditForm({}); };

  const save = async (id) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/aug/admin/partners/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editForm),
      });
      if (res.ok) { setMsg('Salvo!'); load(); cancelEdit(); setTimeout(() => setMsg(''), 3000); }
      else { const d = await res.json(); setMsg(d.detail || 'Erro ao salvar'); }
    } finally { setSaving(false); }
  };

  const deactivate = async (id) => {
    if (!window.confirm('Desativar este parceiro?')) return;
    const res = await fetch(`${API_URL}/api/aug/admin/partners/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { setMsg('Parceiro desativado'); load(); setTimeout(() => setMsg(''), 3000); }
  };

  const [showNewForm, setShowNewForm] = useState(false);
  const [newForm, setNewForm] = useState({ full_name: '', email: '', password: '', phone: '', plan_type: 'amador' });
  const [creating, setCreating] = useState(false);

  const createPartner = async () => {
    if (!newForm.full_name || !newForm.email || !newForm.password) { setMsg('Nome, email e senha são obrigatórios'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/partner/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newForm),
      });
      const d = await res.json();
      if (res.ok) {
        setMsg('Parceiro criado!');
        setShowNewForm(false);
        setNewForm({ full_name: '', email: '', password: '', phone: '', plan_type: 'amador' });
        load();
        setTimeout(() => setMsg(''), 4000);
      } else {
        setMsg(d.detail || 'Erro ao criar parceiro');
      }
    } finally { setCreating(false); }
  };

  if (loading) return <div className="text-white/50 text-center py-20">Carregando...</div>;

  return (
    <div className="space-y-4">
      {/* New Partner Modal */}
      {showNewForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowNewForm(false)}>
          <div className="bg-slate-800 border border-white/10 rounded-2xl p-6 w-full max-w-md mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-bold text-lg">Novo Parceiro</h3>
              <button onClick={() => setShowNewForm(false)} className="text-white/40 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <input value={newForm.full_name} onChange={e => setNewForm({ ...newForm, full_name: e.target.value })}
                placeholder="Nome completo *" className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/30 text-sm" data-testid="new-partner-name" />
              <input value={newForm.email} onChange={e => setNewForm({ ...newForm, email: e.target.value })}
                placeholder="Email *" type="email" className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/30 text-sm" data-testid="new-partner-email" />
              <input value={newForm.password} onChange={e => setNewForm({ ...newForm, password: e.target.value })}
                placeholder="Senha * (mín. 6 caracteres)" type="password" className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/30 text-sm" data-testid="new-partner-password" />
              <input value={newForm.phone} onChange={e => setNewForm({ ...newForm, phone: e.target.value })}
                placeholder="Telefone (opcional)" className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder-white/30 text-sm" data-testid="new-partner-phone" />
              <select value={newForm.plan_type} onChange={e => setNewForm({ ...newForm, plan_type: e.target.value })}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm" data-testid="new-partner-plan">
                {PLANS.map(pl => <option key={pl} value={pl} className="bg-slate-800">{PLAN_NAMES[pl]}</option>)}
              </select>
              {msg && <p className="text-red-400 text-xs">{msg}</p>}
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={() => setShowNewForm(false)} className="flex-1 py-2 rounded-lg border border-white/20 text-white/60 hover:bg-white/5 transition-colors text-sm">Cancelar</button>
              <button onClick={createPartner} disabled={creating}
                className="flex-1 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors text-sm disabled:opacity-50" data-testid="confirm-create-partner">
                {creating ? 'Criando...' : 'Criar Parceiro'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white">Parceiros ({partners.length})</h2>
        <div className="flex items-center gap-2">
          {msg && !showNewForm && <span className="text-sm text-green-400 bg-green-500/10 px-3 py-1 rounded-full">{msg}</span>}
          <button onClick={() => { setShowNewForm(true); setMsg(''); }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition-colors" data-testid="new-partner-btn">
            <Plus className="w-4 h-4" /> Novo Parceiro
          </button>
          <button onClick={load} className="p-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors" data-testid="refresh-partners"><RefreshCw className="w-4 h-4 text-white/60" /></button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-white/40 pb-3 pr-4">Parceiro</th>
              <th className="text-left text-white/40 pb-3 pr-4">Slug</th>
              <th className="text-left text-white/40 pb-3 pr-4">Plano</th>
              <th className="text-left text-white/40 pb-3 pr-4">Status</th>
              <th className="text-left text-white/40 pb-3 pr-4">Comissão</th>
              <th className="text-left text-white/40 pb-3 pr-4">Prods.</th>
              <th className="text-left text-white/40 pb-3 pr-4">Admin</th>
              <th className="text-left text-white/40 pb-3">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {partners.map(p => (
              <tr key={p.id} className="hover:bg-white/5 transition-colors">
                <td className="py-3 pr-4">
                  <div className="font-medium text-white">{p.full_name}</div>
                  <div className="text-white/40 text-xs">{p.email}</div>
                </td>
                <td className="py-3 pr-4 text-white/60">{p.slug || '-'}</td>
                <td className="py-3 pr-4">
                  {editId === p.id ? (
                    <select value={editForm.plan_type} onChange={e => setEditForm({ ...editForm, plan_type: e.target.value })}
                      className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs w-36">
                      {PLANS.map(pl => <option key={pl} value={pl} className="bg-slate-800">{PLAN_NAMES[pl]}</option>)}
                    </select>
                  ) : (
                    <span className="text-white/80">{PLAN_NAMES[p.plan_type] || p.plan_type}</span>
                  )}
                </td>
                <td className="py-3 pr-4">
                  {editId === p.id ? (
                    <select value={editForm.subscription_status} onChange={e => setEditForm({ ...editForm, subscription_status: e.target.value })}
                      className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs">
                      <option value="active" className="bg-slate-800">Ativo</option>
                      <option value="inactive" className="bg-slate-800">Inativo</option>
                      <option value="trial" className="bg-slate-800">Trial</option>
                    </select>
                  ) : (
                    <span className={statusColor(p.subscription_status)}>{p.subscription_status}</span>
                  )}
                </td>
                <td className="py-3 pr-4">
                  {editId === p.id ? (
                    <input type="number" min="0" max="0.5" step="0.01" value={editForm.commission_rate}
                      onChange={e => setEditForm({ ...editForm, commission_rate: parseFloat(e.target.value) })}
                      className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-xs w-20" />
                  ) : (
                    <span className="text-white/60">{((p.commission_rate || 0) * 100).toFixed(0)}%</span>
                  )}
                </td>
                <td className="py-3 pr-4 text-white/60">{p.product_count}</td>
                <td className="py-3 pr-4">
                  {editId === p.id ? (
                    <button onClick={() => setEditForm({ ...editForm, is_admin: !editForm.is_admin })}
                      className={`w-6 h-6 rounded flex items-center justify-center border ${editForm.is_admin ? 'bg-blue-500 border-blue-500' : 'border-white/20'}`}>
                      {editForm.is_admin && <Check className="w-3 h-3 text-white" />}
                    </button>
                  ) : (
                    p.is_admin ? <Shield className="w-4 h-4 text-blue-400" /> : <span className="text-white/20">-</span>
                  )}
                </td>
                <td className="py-3">
                  {editId === p.id ? (
                    <div className="flex gap-1">
                      <button onClick={() => save(p.id)} disabled={saving}
                        className="p-1.5 rounded bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors" data-testid={`save-partner-${p.id}`}>
                        <Check className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={cancelEdit} className="p-1.5 rounded bg-white/10 hover:bg-white/20 text-white/60 transition-colors">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <button onClick={() => startEdit(p)} className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors" data-testid={`edit-partner-${p.id}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deactivate(p.id)} className="p-1.5 rounded hover:bg-red-500/20 text-white/40 hover:text-red-400 transition-colors" data-testid={`deactivate-partner-${p.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {partners.length === 0 && <p className="text-white/30 text-center py-10">Nenhum parceiro cadastrado</p>}
      </div>
    </div>
  );
};

/* ── Products Tab ── */
const ProductsTab = ({ token }) => {
  const [data, setData] = useState({ products: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterSlug, setFilterSlug] = useState('');
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 30 });
    if (filterSlug) params.set('partner_slug', filterSlug);
    fetch(`${API_URL}/api/aug/admin/products?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setData).finally(() => setLoading(false));
  }, [token, page, filterSlug]);

  useEffect(() => { load(); }, [load]);

  const save = async (id) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/aug/admin/products/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(editForm),
      });
      if (res.ok) { setMsg('Salvo!'); load(); setEditId(null); setTimeout(() => setMsg(''), 3000); }
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Excluir este produto?')) return;
    const res = await fetch(`${API_URL}/api/aug/admin/products/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) { setMsg('Produto excluído'); load(); setTimeout(() => setMsg(''), 3000); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white">Produtos ({data.total})</h2>
        <div className="flex items-center gap-3">
          {msg && <span className="text-sm text-green-400 bg-green-500/10 px-3 py-1 rounded-full">{msg}</span>}
          <input value={filterSlug} onChange={e => { setFilterSlug(e.target.value); setPage(1); }} placeholder="Filtrar por slug..."
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/30 w-44" data-testid="product-filter-slug" />
          <button onClick={load} className="p-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors"><RefreshCw className="w-4 h-4 text-white/60" /></button>
        </div>
      </div>
      {loading ? (
        <div className="text-white/50 text-center py-20">Carregando...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/40 pb-3 pr-4">Produto</th>
                  <th className="text-left text-white/40 pb-3 pr-4">Parceiro</th>
                  <th className="text-left text-white/40 pb-3 pr-4">Preço</th>
                  <th className="text-left text-white/40 pb-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.products.map(p => (
                  <tr key={p.id} className="hover:bg-white/5 transition-colors">
                    <td className="py-3 pr-4">
                      {editId === p.id ? (
                        <input value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                          className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm w-48" />
                      ) : (
                        <span className="text-white font-medium line-clamp-1">{p.name}</span>
                      )}
                    </td>
                    <td className="py-3 pr-4 text-white/50">{p.partner_slug || '-'}</td>
                    <td className="py-3 pr-4">
                      {editId === p.id ? (
                        <input type="number" value={editForm.price || ''} onChange={e => setEditForm({ ...editForm, price: parseFloat(e.target.value) })}
                          className="bg-white/10 border border-white/20 rounded px-2 py-1 text-white text-sm w-28" />
                      ) : (
                        <span className="text-white/80">{p.price ? fmt(p.price) : <span className="text-white/30">S/ preço</span>}</span>
                      )}
                    </td>
                    <td className="py-3">
                      {editId === p.id ? (
                        <div className="flex gap-1">
                          <button onClick={() => save(p.id)} disabled={saving} className="p-1.5 rounded bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors"><Check className="w-3.5 h-3.5" /></button>
                          <button onClick={() => setEditId(null)} className="p-1.5 rounded bg-white/10 hover:bg-white/20 text-white/60 transition-colors"><X className="w-3.5 h-3.5" /></button>
                        </div>
                      ) : (
                        <div className="flex gap-1">
                          <button onClick={() => { setEditId(p.id); setEditForm({ name: p.name, price: p.price }); }} className="p-1.5 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors" data-testid={`edit-product-${p.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                          <button onClick={() => del(p.id)} className="p-1.5 rounded hover:bg-red-500/20 text-white/40 hover:text-red-400 transition-colors" data-testid={`delete-product-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.products.length === 0 && <p className="text-white/30 text-center py-10">Nenhum produto encontrado</p>}
          </div>
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border border-white/10 disabled:opacity-30 hover:bg-white/5 transition-colors"><ChevronLeft className="w-4 h-4 text-white" /></button>
              <span className="text-white/50 text-sm">Pág. {page} de {data.pages}</span>
              <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages} className="p-2 rounded-lg border border-white/10 disabled:opacity-30 hover:bg-white/5 transition-colors"><ChevronRight className="w-4 h-4 text-white" /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ── Orders Tab ── */
const OrdersTab = ({ token }) => {
  const [data, setData] = useState({ orders: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filterSlug, setFilterSlug] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 30 });
    if (filterSlug) params.set('partner_slug', filterSlug);
    if (filterStatus) params.set('status', filterStatus);
    fetch(`${API_URL}/api/aug/admin/orders?${params}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setData).finally(() => setLoading(false));
  }, [token, page, filterSlug, filterStatus]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-bold text-white">Pedidos ({data.total})</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={filterSlug} onChange={e => { setFilterSlug(e.target.value); setPage(1); }} placeholder="Parceiro..."
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm placeholder-white/30 w-36" data-testid="order-filter-slug" />
          <select value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}
            className="bg-white/10 border border-white/20 rounded-lg px-3 py-1.5 text-white text-sm w-36">
            <option value="" className="bg-slate-800">Todos status</option>
            <option value="approved" className="bg-slate-800">Aprovado</option>
            <option value="pending" className="bg-slate-800">Pendente</option>
            <option value="completed" className="bg-slate-800">Concluído</option>
            <option value="failed" className="bg-slate-800">Falhou</option>
            <option value="cancelled" className="bg-slate-800">Cancelado</option>
          </select>
          <button onClick={load} className="p-2 rounded-lg border border-white/10 hover:bg-white/5 transition-colors"><RefreshCw className="w-4 h-4 text-white/60" /></button>
        </div>
      </div>
      {loading ? (
        <div className="text-white/50 text-center py-20">Carregando...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left text-white/40 pb-3 pr-4">Data</th>
                  <th className="text-left text-white/40 pb-3 pr-4">Comprador</th>
                  <th className="text-left text-white/40 pb-3 pr-4">Parceiro</th>
                  <th className="text-left text-white/40 pb-3 pr-4">Total</th>
                  <th className="text-left text-white/40 pb-3 pr-4">Status</th>
                  <th className="text-left text-white/40 pb-3">Tipo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {data.orders.map(o => (
                  <tr key={o.id} className="hover:bg-white/5 transition-colors">
                    <td className="py-3 pr-4 text-white/50 whitespace-nowrap">{fmtDate(o.created_at)}</td>
                    <td className="py-3 pr-4">
                      <div className="text-white/80">{o.payer_info?.name || o.buyer?.name || '-'}</div>
                      <div className="text-white/30 text-xs">{o.payer_info?.email || o.buyer?.email || ''}</div>
                    </td>
                    <td className="py-3 pr-4 text-white/50">{o.partner_slug || '-'}</td>
                    <td className="py-3 pr-4 text-white font-medium">{fmt(o.total)}</td>
                    <td className="py-3 pr-4"><span className={statusColor(o.status)}>{o.status}</span></td>
                    <td className="py-3 text-white/40">{o.type || 'mp'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.orders.length === 0 && <p className="text-white/30 text-center py-10">Nenhum pedido encontrado</p>}
          </div>
          {data.pages > 1 && (
            <div className="flex items-center justify-center gap-3">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg border border-white/10 disabled:opacity-30 hover:bg-white/5 transition-colors"><ChevronLeft className="w-4 h-4 text-white" /></button>
              <span className="text-white/50 text-sm">Pág. {page} de {data.pages}</span>
              <button onClick={() => setPage(p => Math.min(data.pages, p + 1))} disabled={page === data.pages} className="p-2 rounded-lg border border-white/10 disabled:opacity-30 hover:bg-white/5 transition-colors"><ChevronRight className="w-4 h-4 text-white" /></button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ── Main Page ── */
export default function AugAdminPage() {
  const { partner, token, loading, logout } = usePartnerAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  useEffect(() => {
    if (!loading && !partner) navigate('/parceiro/login');
  }, [loading, partner, navigate]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0f172a' }}>
      <div className="text-white/50">Verificando acesso...</div>
    </div>
  );

  if (!partner) return null;

  if (!partner.is_admin) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0f172a' }}>
      <div className="text-center">
        <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Acesso Restrito</h2>
        <p className="text-white/50 mb-6">Esta área é exclusiva para administradores AUG.</p>
        <button onClick={() => navigate('/parceiro/dashboard')} className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors">
          Voltar ao Dashboard
        </button>
      </div>
    </div>
  );

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'partners', label: 'Parceiros', icon: Users },
    { id: 'products', label: 'Produtos', icon: Package },
    { id: 'orders', label: 'Pedidos', icon: ShoppingCart },
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1a2744 50%, #0f172a 100%)' }}>
      {/* Header */}
      <header className="border-b border-white/10 bg-white/5 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
              <Shield className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h1 className="text-white font-bold text-lg leading-none">AUG Admin</h1>
              <p className="text-white/30 text-xs">Painel Global</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                <span className="text-blue-400 text-xs font-bold">{partner.full_name?.[0]?.toUpperCase()}</span>
              </div>
              <span className="text-white/60 hidden sm:block">{partner.full_name}</span>
            </div>
            <button onClick={logout} className="p-2 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors" data-testid="admin-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 mb-8 bg-white/5 p-1 rounded-xl w-fit">
          {tabs.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTab === t.id ? 'bg-blue-500 text-white shadow-lg' : 'text-white/50 hover:text-white hover:bg-white/10'}`}>
                <Icon className="w-4 h-4" />
                <span className="hidden sm:block">{t.label}</span>
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          {activeTab === 'dashboard' && <DashboardTab token={token} />}
          {activeTab === 'partners' && <PartnersTab token={token} />}
          {activeTab === 'products' && <ProductsTab token={token} />}
          {activeTab === 'orders' && <OrdersTab token={token} />}
        </div>
      </div>
    </div>
  );
}
